import apiClient from './client'

export type SubsystemCategory =
  | 'internet'
  | 'hvac'
  | 'water'
  | 'power'
  | 'security'
  | 'automation'
  | 'multimedia'
  | 'lighting'
  | 'other'

export type SubsystemStatus =
  | 'operational'
  | 'maintenance'
  | 'installation'
  | 'planned'
  | 'fault'

export interface SubsystemItem {
  id: number
  project_id: number
  category?: string
  name: string
  description?: string | null
  status: SubsystemStatus
  contract_info?: string | null
  credentials_info?: string | null
  contacts_info?: string | null
  passport_data?: string | null
  passport_data_parsed?: Record<string, any> | null
  created_by?: number | null
  creator_name?: string | null
  created_at: string
  updated_at: string
  post_count?: number
  last_post_at?: string | null
  task_count?: number
  active_task_count?: number
}

export interface CreateSubsystemInput {
  project_id: number
  name: string
  category?: string
  description?: string
  status?: SubsystemStatus
  contract_info?: string
  credentials_info?: string
  contacts_info?: string
  passport_data?: Record<string, any> | string
}

export interface UpdateSubsystemInput {
  name?: string
  category?: string
  description?: string | null
  status?: SubsystemStatus
  contract_info?: string | null
  credentials_info?: string | null
  contacts_info?: string | null
  passport_data?: Record<string, any> | string | null
}

export const CATEGORY_DEFINITIONS: Record<
  SubsystemCategory,
  { label: string; icon: string; defaultName: string; defaultDescription: string }
> = {
  internet: {
    label: 'Интернет и связь',
    icon: '🌐',
    defaultName: 'Интернет и провайдеры',
    defaultDescription: 'Провайдеры, договоры, резервные каналы связи, оптика, роутеры и точки доступа',
  },
  hvac: {
    label: 'Климат и вентиляция',
    icon: '❄️',
    defaultName: 'Система кондиционирования и вентиляции',
    defaultDescription: 'Мульти-сплит системы, внешние и внутренние блоки, приточно-вытяжная вентиляция, дренажи и помпы',
  },
  water: {
    label: 'Водоснабжение и защита от протечек',
    icon: '💧',
    defaultName: 'Водоснабжение и защита Нептун',
    defaultDescription: 'Краны с электроприводом, модули управления, датчики протечки, насосы и фильтры очистки',
  },
  power: {
    label: 'Электроснабжение и ИБП',
    icon: '⚡',
    defaultName: 'Бесперебойное питание и ввод',
    defaultDescription: 'Вводные мощности, ИБП, стабилизаторы, фазировка, резервное питание',
  },
  security: {
    label: 'Видеонаблюдение и СКУД',
    icon: '📹',
    defaultName: 'Видеонаблюдение, домофон и СКУД',
    defaultDescription: 'IP-камеры, видеорегистраторы (NVR), вызывные панели, электронные замки и доступ',
  },
  automation: {
    label: 'Автоматизация и Умный дом',
    icon: '🤖',
    defaultName: 'Система автоматизации (Wiren Board / KNX)',
    defaultDescription: 'Контроллеры, модули ввода-вывода, реле, диммеры, интеграционные шлюзы и сценарии',
  },
  multimedia: {
    label: 'Мультимедиа и акустика',
    icon: '🔊',
    defaultName: 'Мультирум и мультимедиа',
    defaultDescription: 'Ресиверы, акустические зоны, ТВ, медиаплееры, коммутация звука и видео',
  },
  lighting: {
    label: 'Освещение и сценарии',
    icon: '💡',
    defaultName: 'Специальное и ландшафтное освещение',
    defaultDescription: 'Архитектурная подсветка, DALI, светодиодные ленты и блоки питания',
  },
  other: {
    label: 'Прочая система',
    icon: '📦',
    defaultName: 'Инженерная система',
    defaultDescription: 'Инженерное оборудование объекта',
  },
}

export const STATUS_DEFINITIONS: Record<
  SubsystemStatus,
  { label: string; color: string; badgeClass: string }
> = {
  operational: {
    label: 'Работает штатно',
    color: 'emerald',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  maintenance: {
    label: 'На обслуживании',
    color: 'amber',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  installation: {
    label: 'Монтаж и пусконаладка',
    color: 'blue',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  planned: {
    label: 'Планируется',
    color: 'purple',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  fault: {
    label: 'Авария / Сбой',
    color: 'red',
    badgeClass: 'bg-red-100 text-red-800 border-red-200',
  },
}

export const getSubsystems = async (projectId: number): Promise<SubsystemItem[]> => {
  const response = await apiClient.get('/api/subsystems', {
    params: { project_id: projectId },
  })
  return Array.isArray(response.data) ? response.data : []
}

export const getSubsystem = async (subsystemId: number): Promise<SubsystemItem> => {
  const response = await apiClient.get(`/api/subsystems/${subsystemId}`)
  return response.data
}

export const createSubsystem = async (data: CreateSubsystemInput): Promise<{ id: number }> => {
  const response = await apiClient.post('/api/subsystems', data)
  return response.data
}

export const updateSubsystem = async (
  subsystemId: number,
  data: UpdateSubsystemInput
): Promise<void> => {
  await apiClient.put(`/api/subsystems/${subsystemId}`, data)
}

export const deleteSubsystem = async (subsystemId: number): Promise<void> => {
  await apiClient.delete(`/api/subsystems/${subsystemId}`)
}
